// Helpful info:
// https://confluence.ci.motional.com/confluence/display/ML/Auth+Service+Module
import { loadConfig } from 'packages/lib/config';
import type { OktaAuthOptions } from '@okta/okta-auth-js';
import { OktaAuth, Tokens } from '@okta/okta-auth-js';
import {
  AuthService,
  User,
  AuthResult,
  hasChrome,
  IssuerName,
} from 'packages/lib/auth/data';
import history from 'packages/lib/utils/history';
import { isContractorPath, isMobisPath } from 'packages/lib/auth/is-path';
import { findKey } from 'lodash';
import { logError } from 'packages/lib/utils/logger';
import { getTenantBasepath } from 'packages/lib/auth/is-path';
import { isRunningUnitTests } from 'packages/lib/utils/is-running-unit-tests';

const { auth } = loadConfig();
const { primary, contractor, mobis } = auth;

const getTenantAuthConfig = () => {
  if (isContractorPath(window.location.pathname)) {
    return contractor;
  }
  if (isMobisPath(window.location.pathname)) {
    return mobis;
  }
  return primary;
};

const {
  client_id: clientId,
  issuer,
  redirect_uri: redirectUri,
  scope: scopes,
} = getTenantAuthConfig();

const getIssuerName = (issuerUrl: string): IssuerName => {
  const issuerName = findKey(auth, (a) => a.issuer === issuerUrl) as
    | IssuerName
    | undefined;
  if (!issuerName) {
    throw new Error(`Invalid token issuer: ${issuerUrl}`);
  }
  return issuerName;
};

const oktaConfig: OktaAuthOptions = {
  clientId,
  issuer,
  redirectUri,
  scopes,
  pkce: true,
  // transformAuthState: async (
  //   oktaAuth: OktaAuth,
  //   authState: AuthState
  // ): Promise<AuthState> => {
  //   // for debugging purposes only
  //   // console.log(oktaAuth, authState, await oktaAuth.token.getUserInfo());
  //   return authState;
  // },
};

export const oktaClient = new OktaAuth(oktaConfig);

const getAuthData = async (): Promise<AuthResult | null> => {
  const { idToken, accessToken } = await oktaClient.tokenManager.getTokens();
  const authData = normalizeAuthData({
    idToken,
    accessToken,
  });
  return authData;
};

(function startOktaService(): void {
  /**
   * Subscribe to token manager events
   */
  // oktaClient.tokenManager.on(EVENT_ERROR, (err: Error) => {
  //   const serializedErr = JSON.stringify(err);
  //   const isAccessTokenError = serializedErr
  //     .toLocaleLowerCase()
  //     .includes('accesstoken');
  //   const cachedTokens = localStorage.getItem('okta-token-storage');
  //   const parsedTokens = cachedTokens && (JSON.parse(cachedTokens) as Tokens);
  //   if (isAccessTokenError && parsedTokens) {
  //     // don't log to sentry if the user simply needs to login
  //     if (serializedErr.includes(`login_required`)) return;

  //     const expiresAt = parsedTokens.accessToken?.expiresAt;
  //     if (!expiresAt) {
  //       // theoretically this should never occur
  //       Sentry.captureMessage('accessToken expiresAt is missing.');
  //     } else {
  //       const expiresAtDate = new Date(expiresAt * 1000);
  //       const now = new Date();
  //       Sentry.captureMessage(
  //         `[DEBUG LOG 1] [token manager ${EVENT_ERROR} event]: ${serializedErr}
  //       user email: ${parsedTokens.idToken?.claims?.email}
  //       accessToken expires: ${JSON.stringify(expiresAtDate.toString())}
  //       current datetime: ${now}`
  //       );
  //     }
  //   } else {
  //     Sentry.captureMessage(
  //       `[DEBUG LOG 2] [token manager ${EVENT_ERROR} event]: ${serializedErr}
  //       user email: ${parsedTokens && parsedTokens?.idToken?.claims?.email}`
  //     );
  //   }
  // });

  /**
   * Start okta service
   */
  oktaClient.start().catch((err) => logError(err));
})();

export const authClient = async (): Promise<OktaAuth> => oktaClient;

export const getTokenSilently: AuthService['getTokenSilently'] = async () => {
  if (isRunningUnitTests()) {
    return null;
  }

  if (!hasChrome()) {
    history.replace('/unsupported-browser?auth=okta');
  }

  if (
    oktaClient.isLoginRedirect() ||
    window.location.pathname.includes('callback')
  ) {
    return null;
  }

  if (await oktaClient.isAuthenticated()) {
    return getAuthData();
  }

  await redirectToLogin();

  return null;
};

let redirectLock = false; // lock to prevent redirect spamming on app load
export const redirectToLogin: AuthService['redirectToLogin'] = async () => {
  if (redirectLock) {
    return;
  }
  redirectLock = true;

  oktaClient.setOriginalUri(window.location.href);
  await oktaClient.token.getWithRedirect();
};

export const signOut: AuthService['signOut'] = async (): Promise<void> => {
  const postLogoutRedirectUri = window.location.origin + getTenantBasepath();
  // it's necessary to manually revoke refresh and access tokens when using a custom auth server (i.e. apps.motional.com)
  // see here: https://github.com/okta/okta-auth-js/issues/393
  const tokens = await oktaClient.tokenManager.getTokens();
  const signOutOptions = tokens
    ? {
        postLogoutRedirectUri,
        revokeRefreshToken: true,
        revokeAccessToken: true,
        accessToken: tokens.accessToken,
        idToken: tokens.idToken,
      }
    : {
        postLogoutRedirectUri,
        revokeRefreshToken: true,
        revokeAccessToken: true,
      };
  await oktaClient.signOut(signOutOptions).catch(async (err) => {
    logError(
      `Failed okta.signOut, attempting manual signout, error: ${JSON.stringify(
        err
      )}`
    );
    await oktaClient.tokenManager.clear();
    [
      'okta-token-storage',
      'okta-cache-storage',
      'okta-shared-transaction-storage',
    ].forEach((key) => localStorage.removeItem(key));
    await oktaClient.authStateManager.updateAuthState();
    await oktaClient
      .signOut({
        postLogoutRedirectUri,
      })
      .catch((err) => {
        window.location.replace(postLogoutRedirectUri);
        logError(`Failed manual okta signout, error: ${JSON.stringify(err)}`);
      });
  });
  return;
};

function normalizeAuthData(tokens: Tokens): AuthResult | null {
  const { accessToken, idToken } = tokens;
  if (accessToken?.accessToken && idToken?.idToken) {
    // ts-ignore because accessToken.claims.scope is a custom field
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const customScopes = (accessToken?.claims.scope as string).split(' ');
    const authData: AuthResult = {
      user: {
        email: idToken.claims.email,
        name: idToken.claims.name,
      },
      accessToken: accessToken.accessToken,
      expiresAt: new Date(accessToken.expiresAt * 1000).toUTCString(),
      idToken: idToken.idToken,
      scopes: [...accessToken.scopes, ...customScopes],
      issuer: getIssuerName(idToken.issuer),
    };
    return authData;
  }
  return null;
}

export const user: AuthService['user'] = async (): Promise<User | null> => {
  const authData = await getTokenSilently();
  return authData?.user || null;
};

/** Can be used to determine if the token is for a contractor or internal motional user.
 * @returns Promise containing: "primary" | "contractor" | null
 */
export const getTenantName = async (): Promise<IssuerName | null> => {
  const authData = await getTokenSilently();
  return authData?.issuer || null;
};

const scopeMap = {
  write: ['write:annotations:lidar', 'write:annotations'],
};
export const userHasScopes: AuthService['userHasScopes'] = async (
  scopes: string[]
): Promise<boolean> => {
  const authData = await getAuthData();
  //console.log('SCOPES', authData?.scopes);
  return scopes.every((scope) => {
    const mappedScopes: string[] = scopeMap[scope] ?? [scope];
    return mappedScopes.find((s) => authData?.scopes.includes(s));
  });
};

export const userScopeIncludesOneOf: AuthService['userScopeIncludesOneOf'] =
  async (scopes: string[], tries = 40): Promise<boolean> => {
    const authData = await getAuthData();
    return scopes.some((scope) =>
      authData?.scopes.includes(scopeMap[scope] || scope)
    );
  };

export const userEmail = (): Promise<string | undefined> =>
  user().then((user) => user?.email);
